import {
  Origin,
  type TemplateItem,
  type TemplateSection,
} from 'inspection-types';

import type { VisibilityOperationKind } from './operations';
import { resolveOrigin } from './origin';

export type QuestionPlacement = {
  question: TemplateItem;
  section: Pick<TemplateSection, 'removed'>;
};

export type SectionPlacement = Pick<TemplateSection, 'removed' | 'origin'>;

type OrderedNode = Pick<TemplateSection | TemplateItem, 'removed' | 'origin'>;

export type OperationOrder<TKind extends string> = Record<
  'visible' | 'hidden',
  Record<Origin.INHERITED | Origin.LOCAL, readonly TKind[]>
>;

// Display order of the kinds a section or a question can offer. Edit and Delete join the LOCAL
// lists when their tickets add the kinds.
const SECTION_OPERATION_ORDER: OperationOrder<VisibilityOperationKind> = {
  visible: {
    [Origin.INHERITED]: ['hideSection'],
    [Origin.LOCAL]: ['hideSection'],
  },
  hidden: {
    [Origin.INHERITED]: ['unhideSection'],
    [Origin.LOCAL]: ['unhideSection'],
  },
};

const QUESTION_OPERATION_ORDER: OperationOrder<VisibilityOperationKind> = {
  visible: {
    [Origin.INHERITED]: ['hideQuestion'],
    [Origin.LOCAL]: ['hideQuestion'],
  },
  hidden: {
    [Origin.INHERITED]: ['unhideQuestion'],
    [Origin.LOCAL]: ['unhideQuestion'],
  },
};

export function selectOperations<TKind extends string>(
  order: OperationOrder<TKind>,
  placement: OrderedNode
): TKind[] {
  const state = placement.removed ? 'hidden' : 'visible';
  const origin = resolveOrigin(placement);

  return [...order[state][origin]];
}

export function offeredSectionOperations(
  placement: SectionPlacement
): VisibilityOperationKind[] {
  return selectOperations(SECTION_OPERATION_ORDER, placement);
}

export function offeredQuestionOperations({
  question,
  section,
}: QuestionPlacement): VisibilityOperationKind[] {
  if (section.removed) {
    return [];
  }

  return selectOperations(QUESTION_OPERATION_ORDER, question);
}
